import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Capture scroll position safely and restore after DOM/layout work.
   * This prevents Shopify Theme Editor / view transitions from jumping the page.
   */
  #withScrollLock = async (fn) => {
    const x = window.scrollX;
    const y = window.scrollY;

    try {
      await fn();
    } finally {
      // Restore on next frames to beat layout/paint and any focus/transition side effects
      requestAnimationFrame(() => {
        window.scrollTo(x, y);
        requestAnimationFrame(() => window.scrollTo(x, y));
      });
    }
  };

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;
    if (!grid) return;

    await this.#withScrollLock(async () => {
      // If View Transitions are flaky in Theme Editor, this prevents scroll jumps
      await startViewTransition(() => this.#setLayout(value), ['product-grid']);
    });

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    // Prevent any weird scroll side effects while toggling controls + layout
    void this.#withScrollLock(async () => {
      targetElement.checked = true;
      this.#setLayout('default');
    });
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}